import { getCartToken } from './cart';
import { normalizeCartError, handleCartError } from './cartActions';

const HEARTBEAT_INTERVAL = 60000;
const JITTER_MIN = 10000;
const JITTER_MAX = 20000;
const MIN_INTERVAL_BETWEEN_SENDS = 30000;
const HEARTBEAT_ENDPOINT = `${process.env.GATSBY_CMS_BASE_URL}/wp-json/autobell/v1/api/cart/heartbeat/`;
const CART_STORAGE_KEY = 'ab_cart';

class CartHeartbeat {
    constructor() {
        this.intervalId = null;
        this.cartId = null;
        this.isRunning = false;
        this.visibilityHandler = null;
        this.inFlight = false;
        this.lastSentAt = 0;
    }

    getJitter() {
        return (
            Math.floor(Math.random() * (JITTER_MAX - JITTER_MIN + 1)) +
            JITTER_MIN
        );
    }

    getNextInterval() {
        return HEARTBEAT_INTERVAL + this.getJitter();
    }

    async sendHeartbeat() {
        if (this.inFlight) {
            return;
        }
        const elapsed = Date.now() - this.lastSentAt;
        if (this.lastSentAt && elapsed < MIN_INTERVAL_BETWEEN_SENDS) {
            return;
        }

        // Check if cart still exists in localStorage
        try {
            const cart = JSON.parse(
                localStorage.getItem(CART_STORAGE_KEY) || '{}'
            );
            if (!cart.cart_id) {
                this.stop();
                return;
            }
            // Update internal cart_id if it changed
            if (cart.cart_id !== this.cartId) {
                this.cartId = cart.cart_id;
            }
        } catch (error) {
            console.error(
                '[Heartbeat] Failed to read cart from storage:',
                error
            );
            this.stop();
            return;
        }

        if (!this.cartId) {
            this.stop();
            return;
        }

        if (document.hidden) {
            return;
        }

        const payload = {
            cart_id: this.cartId,
            client_meta: {
                path: window.location.pathname,
                referrer: document.referrer
            }
        };

        const token = getCartToken();
        if (token) {
            payload.cart_token = token;
        }

        this.inFlight = true;
        this.lastSentAt = Date.now();
        try {
            const response = await fetch(HEARTBEAT_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (response.status === 204) {
                return;
            }

            let body = null;
            try {
                body = await response.json();
            } catch (parseError) {
                // ignore JSON parse errors; body will remain null
            }

            const normalizedError = normalizeCartError(response, body);
            handleCartError(normalizedError, 'heartbeat');

            if (response.status === 410) {
                this.stop();
            }
        } catch (error) {
            const normalizedError = normalizeCartError(
                { status: 0 },
                {
                    error: {
                        type: 'network_error',
                        message: error.message || 'Network request failed'
                    }
                }
            );
            handleCartError(normalizedError, 'heartbeat');
        } finally {
            this.inFlight = false;
        }
    }

    start(cartId) {
        if (!cartId) {
            return;
        }

        if (this.isRunning && this.cartId === cartId) {
            return;
        }

        this.stop();

        this.cartId = cartId;
        this.isRunning = true;

        const scheduleNext = () => {
            if (!this.isRunning) return;

            if (document.hidden) {
                return;
            }

            this.sendHeartbeat().then(() => {
                if (this.isRunning && !document.hidden) {
                    this.intervalId = setTimeout(
                        scheduleNext,
                        this.getNextInterval()
                    );
                }
            });
        };

        this.visibilityHandler = () => {
            if (document.visibilityState === 'visible' && this.isRunning) {
                this.sendHeartbeat().then(() => {
                    if (!this.isRunning) return;
                    this.intervalId = setTimeout(
                        scheduleNext,
                        this.getNextInterval()
                    );
                });
                return;
            }

            if (document.visibilityState === 'hidden' && this.isRunning) {
                if (this.intervalId) {
                    clearTimeout(this.intervalId);
                    this.intervalId = null;
                }
            }
        };

        document.addEventListener('visibilitychange', this.visibilityHandler);

        if (!document.hidden) {
            scheduleNext();
        }
    }

    stop() {
        if (this.intervalId) {
            clearTimeout(this.intervalId);
            this.intervalId = null;
        }
        if (this.visibilityHandler) {
            document.removeEventListener(
                'visibilitychange',
                this.visibilityHandler
            );
            this.visibilityHandler = null;
        }
        this.isRunning = false;
        this.cartId = null;
    }
}

const cartHeartbeat = new CartHeartbeat();

export default cartHeartbeat;
